import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .fav-page {
    min-height: 100vh;
    background: #0a1628;
    font-family: 'DM Sans', sans-serif;
    padding: 40px 20px 60px;
    color: #f0f4f8;
  }

  .fav-container {
    max-width: 860px;
    margin: 0 auto;
  }

  .fav-title {
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 28px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .fav-count {
    font-size: 13px;
    font-weight: 400;
    color: rgba(255,255,255,0.35);
  }

  .fav-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
  }

  .fav-card {
    background: #0f1e35;
    border: 1px solid rgba(0,173,181,0.15);
    border-radius: 14px;
    padding: 22px 20px 18px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: border-color 0.2s, transform 0.2s;
  }

  .fav-card:hover {
    border-color: rgba(0,173,181,0.35);
    transform: translateY(-2px);
  }

  .fav-card__domain {
    font-size: 16px;
    font-weight: 600;
    color: #00adb5;
    text-decoration: none;
    word-break: break-all;
  }

  .fav-card__domain:hover {
    text-decoration: underline;
  }

  .fav-card__price {
    font-size: 14px;
    color: rgba(255,255,255,0.7);
  }

  .fav-card__remove {
    align-self: flex-start;
    margin-top: 4px;
    background: none;
    border: 1px solid rgba(255,87,34,0.3);
    border-radius: 6px;
    color: rgba(255,112,67,0.8);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    padding: 5px 12px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }

  .fav-card__remove:hover {
    background: rgba(255,87,34,0.1);
    color: #ff7043;
  }

  .fav-empty {
    text-align: center;
    padding: 80px 20px;
  }

  .fav-empty__icon {
    font-size: 42px;
    margin-bottom: 16px;
    opacity: 0.3;
  }

  .fav-empty__text {
    color: rgba(255,255,255,0.3);
    font-size: 15px;
    margin-bottom: 20px;
  }

  .fav-empty__link {
    display: inline-block;
    text-decoration: none;
    color: #fff;
    background: #ff5722;
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    transition: background 0.2s;
  }

  .fav-empty__link:hover {
    background: #e64a19;
  }

  .fav-loading {
    text-align: center;
    padding: 60px 20px;
    color: rgba(255,255,255,0.25);
    font-size: 14px;
  }

  .fav-unauthenticated {
    text-align: center;
    padding: 60px 20px;
    color: rgba(255,255,255,0.3);
    font-size: 15px;
  }
`;

export default function Favoritos() {
  const [user, setUser]         = useState(null);
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const auth = getAuth();
  const db   = getFirestore();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u ?? null);
      if (!u) { setLoading(false); return; }

      try {
        const q = query(
          collection(db, 'favoritos'),
          where('uid', '==', u.uid)
        );
        const snap = await getDocs(q);
        setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Erro ao buscar favoritos:', err);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const handleRemove = async (itemId) => {
    try {
      await deleteDoc(doc(db, 'favoritos', itemId));
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (err) {
      console.error('Erro ao remover favorito:', err);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="fav-page">
        <div className="fav-container">
          <h1 className="fav-title">
            Favoritos
            {!loading && user && (
              <span className="fav-count">{items.length} domínio{items.length !== 1 ? 's' : ''}</span>
            )}
          </h1>

          {!user && !loading && (
            <div className="fav-unauthenticated">Você não está autenticado.</div>
          )}

          {loading && (
            <div className="fav-loading">Carregando...</div>
          )}

          {!loading && user && items.length === 0 && (
            <div className="fav-empty">
              <div className="fav-empty__icon">♡</div>
              <div className="fav-empty__text">Você ainda não favoritou nenhum domínio.</div>
              <Link to="/" className="fav-empty__link">Explorar domínios</Link>
            </div>
          )}

          {!loading && user && items.length > 0 && (
            <div className="fav-grid">
              {items.map(item => (
                <div key={item.id} className="fav-card">
                  <Link
                    to={item.slug ? `/dominio/${item.slug}` : '/'}
                    className="fav-card__domain"
                  >
                    {item.dominio || item.domain || 'Domínio'}
                  </Link>

                  {item.preco != null && (
                    <div className="fav-card__price">
                      {Number(item.preco).toLocaleString('pt-BR', {
                        style: 'currency', currency: 'BRL',
                      })}
                    </div>
                  )}

                  <button
                    className="fav-card__remove"
                    onClick={() => handleRemove(item.id)}
                  >
                    Remover
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
